import { useEffect, useRef, useState } from "react";
import { api } from "./api";

/**
 * Real voice search using the browser's Web Speech API,
 * with an automatic fallback to backend Whisper (/api/search/voice)
 * for browsers that don't support SpeechRecognition (Firefox, older iOS).
 *
 *   const v = useVoiceSearch();
 *   v.start(); v.stop();
 *   v.recording, v.transcript, v.error, v.supported, v.via
 */
export function useVoiceSearch({ lang = "en-US", interimResults = true } = {}) {
  const SR = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const supported = !!SR;
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState("");
  const [via, setVia] = useState(null);   // 'web_speech' | 'whisper'
  const [busy, setBusy] = useState(false); // whisper upload pending

  // Cleanup on unmount
  useEffect(() => () => {
    try { recognitionRef.current?.abort?.(); } catch {}
    try { mediaRecorderRef.current?.stop?.(); } catch {}
  }, []);

  const startWebSpeech = () => {
    setError(""); setTranscript(""); setInterim("");
    const r = new SR();
    r.lang = lang;
    r.continuous = false;
    r.interimResults = interimResults;
    r.maxAlternatives = 1;
    r.onstart = () => { setRecording(true); setVia("web_speech"); };
    r.onend = () => { setRecording(false); };
    r.onerror = (e) => {
      setRecording(false);
      const code = e?.error || "speech_error";
      setError(code === "no-speech" ? "No speech detected. Try again." :
               code === "not-allowed" ? "Microphone permission denied." :
               code === "audio-capture" ? "No microphone found." :
               `Voice error: ${code}`);
    };
    r.onresult = (e) => {
      let finalT = "", interimT = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const txt = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalT += txt;
        else interimT += txt;
      }
      if (interimT) setInterim(interimT);
      if (finalT) { setTranscript(t => (t + " " + finalT).trim()); setInterim(""); }
    };
    recognitionRef.current = r;
    try { r.start(); } catch (err) { setError(`Could not start mic: ${err.message}`); setRecording(false); }
  };

  const startWhisper = async () => {
    setError(""); setTranscript(""); setInterim("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Mic API not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
        setBusy(true);
        try {
          const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/search/voice", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setTranscript(data.query || "");
        } catch (e) {
          setError(e?.response?.data?.detail || "Whisper transcription failed");
        } finally { setBusy(false); }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setVia("whisper");
    } catch (e) {
      setError(`Mic access failed: ${e.message}`);
    }
  };

  const start = () => {
    if (recording || busy) return;
    if (supported) startWebSpeech();
    else startWhisper();
  };

  const stop = () => {
    try { recognitionRef.current?.stop?.(); } catch {}
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch {}
  };

  const reset = () => { setTranscript(""); setInterim(""); setError(""); };

  return { supported, recording, busy, transcript, interim, error, via, start, stop, reset };
}
